// Derive UI-shaped "topic" objects (vol24/7d/30d, velocity, sparkline) from
// raw hourly count_buckets. All the rollups and velocity math live here so
// views stay declarative.

import type { CountBucket } from "../shared/types";

const HOUR = 3600;
const DAY = 86400;

export interface Aggregated {
  vol24: number;
  vol7d: number;
  vol30d: number;
  // % change of last 24h vs prior 24h. null if prior window is empty.
  velocity24: number | null;
  // 24 hourly points covering the last 24h, normalised 0..1 (for sparkline)
  spark24: number[];
  // 7 daily-aggregated points (oldest → newest), normalised 0..1
  spark7d: number[];
  // # of hour-buckets we have data for in the last 24/7d/30d windows
  coverage24: number;
  coverage7d: number;
  coverage30d: number;
}

export interface Window {
  windowSec: number;
  // exclusive end (unix sec); typically nowSec aligned to hour
  endSec: number;
}

export function nowAlignedToHourSec(): number {
  return Math.floor(Date.now() / 1000 / HOUR) * HOUR;
}

// Pick a freshness anchor for the dashboard: the MIN of (latest closed bucket
// + 1h) across items whose data is recent. Anchoring metric windows here
// (instead of wall-clock now) keeps "24h vs prior 24h" comparisons honest
// when the last sync is hours behind — otherwise the trailing 3h of zeros
// fakes a decline across every topic.
//
// Stale specs are excluded: if a spec's latest closed bucket is older than
// `staleAfterSec` (default 7d), it doesn't drag the anchor backward. A
// genuinely broken spec stays renderable but doesn't freeze the dashboard.
//
// Returns null when no item qualifies (empty D1, every spec stuck) — caller
// falls back to wall-clock now.
export function computeDataAsOf(
  items: Array<{ buckets: CountBucket[] }>,
  staleAfterSec: number = 7 * 24 * HOUR,
): number | null {
  const cutoff = nowAlignedToHourSec() - staleAfterSec;
  let anchor: number | null = null;
  for (const it of items) {
    let maxClosed = -1;
    for (const b of it.buckets) {
      if (b.is_partial !== 0) continue;
      if (b.bucket_start > maxClosed) maxClosed = b.bucket_start;
    }
    if (maxClosed < 0 || maxClosed < cutoff) continue;
    if (anchor === null || maxClosed < anchor) anchor = maxClosed;
  }
  return anchor === null ? null : anchor + HOUR;
}

function sumIn(
  buckets: CountBucket[],
  startSec: number,
  endSec: number,
): { sum: number; coverage: number } {
  let sum = 0;
  let coverage = 0;
  for (const b of buckets) {
    if (b.bucket_start < startSec) continue;
    if (b.bucket_start >= endSec) continue;
    sum += b.tweet_count;
    coverage += 1;
  }
  return { sum, coverage };
}

export function aggregate(buckets: CountBucket[], endSec?: number): Aggregated {
  const end = endSec ?? nowAlignedToHourSec();
  const start24 = end - 24 * HOUR;
  const start7d = end - 7 * 24 * HOUR;
  const start30d = end - 30 * 24 * HOUR;
  const startPrior24 = end - 48 * HOUR;

  const cur24 = sumIn(buckets, start24, end);
  const prev24 = sumIn(buckets, startPrior24, start24);
  const cur7d = sumIn(buckets, start7d, end);
  const cur30d = sumIn(buckets, start30d, end);

  // Require at least half the window in BOTH cur and prev to have data —
  // otherwise the ratio is dominated by missing buckets, not real movement.
  const MIN_COVERAGE_HOURS = 12;
  let velocity: number | null;
  if (
    cur24.coverage < MIN_COVERAGE_HOURS ||
    prev24.coverage < MIN_COVERAGE_HOURS
  ) {
    velocity = null;
  } else if (prev24.sum === 0) {
    velocity = cur24.sum === 0 ? 0 : null;
  } else {
    velocity = ((cur24.sum - prev24.sum) / prev24.sum) * 100;
  }

  // 24-point hourly spark, last 24h.
  const spark24Raw = new Array<number>(24).fill(0);
  for (const b of buckets) {
    const hoursBefore = Math.floor((end - b.bucket_start) / HOUR);
    if (hoursBefore < 1 || hoursBefore > 24) continue;
    const idx = 24 - hoursBefore;
    if (idx >= 0 && idx < 24) spark24Raw[idx] = b.tweet_count;
  }
  const max24 = Math.max(1, ...spark24Raw);
  const norm24 = spark24Raw.map((v) => v / max24);

  // 7-point daily spark, last 7 days. The newest point includes the
  // in-progress current day (partial) — that's intentional, it shows the
  // most recent movement.
  const spark7dRaw = new Array<number>(7).fill(0);
  for (const b of buckets) {
    const ageSec = end - b.bucket_start;
    if (ageSec < 0 || ageSec >= 7 * DAY) continue;
    const daysBefore = Math.floor(ageSec / DAY);
    const idx = 6 - daysBefore;
    if (idx >= 0 && idx < 7) spark7dRaw[idx] += b.tweet_count;
  }
  const max7d = Math.max(1, ...spark7dRaw);
  const norm7d = spark7dRaw.map((v) => v / max7d);

  return {
    vol24: cur24.sum,
    vol7d: cur7d.sum,
    vol30d: cur30d.sum,
    velocity24: velocity,
    spark24: norm24,
    spark7d: norm7d,
    coverage24: cur24.coverage,
    coverage7d: cur7d.coverage,
    coverage30d: cur30d.coverage,
  };
}
